import React, { useState, useEffect, useRef } from 'react';
import AccountSearch from './AccountSearch';
import MyWorkSearch from './MyWorkSearch';
import classNames from 'classnames';
import RecentViews from './RecentViews';
import InContextSearch from './InContextSearch';
import Commands from './Commands';
import renderIf from 'render-if';
import Modal from '@aha-app/react-bootstrap-modal';
import { FlushTabs } from 'components/Tabs';
import { view } from '@aha-app/react-easy-state';
import { useController, ControlledComponent } from '@aha-app/mvc';
import FredController from './controllers/FredController';
import Command from './Command';
import { useDebouncedValue } from './Hooks';
import SemiControlledInput from './SemiControlledInput';

const MemoizedRecentViews = React.memo(RecentViews);
const MemoizedInContextSearch = React.memo(InContextSearch);
const MemoizedMyWorkSearch = React.memo(MyWorkSearch);

const Fred = () => {
  /** @type {FredController} */
  const controller = useController();
  const { tab, query, initialCommand, initialCommandArgs, callback } =
    controller.state;
  const debounceTimeout = tab === FredController.TAB_ASK_AI ? 1000 : 250;
  const debouncedQuery = useDebouncedValue(query, debounceTimeout);
  const inputRef = useRef(null);

  const pageInfo = window.AhaSearchContext && window.AhaSearchContext.pageInfo;

  // Special handling for quick switch to commands.
  const onInputKeyDown = event => {
    if (
      window.currentAccount?.teamworkOrDevelopFlavor &&
      event.key === '>' &&
      query.length === 0
    ) {
      event.preventDefault();
      event.stopPropagation();
      controller.actionSelectTab(FredController.TAB_COMMANDS);
    } else if (event.key === '/' && query.length === 0) {
      event.preventDefault();
      event.stopPropagation();
      controller.actionSelectTab(FredController.TAB_SEARCH_ACCOUNT);
    }
  };

  useEffect(() => {
    inputRef.current.focus();
    inputRef.current.setSelectionRange(0, inputRef.current.value.length);
  }, []);

  const fredRef = useRef(null);
  const [hoverable, setHoverable] = useState(true);

  const goUp = () => {
    const fred = fredRef.current;
    const hoveredResult = fred.querySelector(':scope .result:hover');
    const focusedResult = fred.querySelector(':scope .result:focus');
    const selectedResult = focusedResult || hoveredResult;
    const tabSelected = fred.querySelector(':scope [role=tab]:focus');

    if (selectedResult && selectedResult.previousElementSibling) {
      selectedResult.previousElementSibling.focus();
    } else if (selectedResult && !selectedResult.previousElementSibling) {
      inputRef.current.focus();
    } else if (tabSelected) {
      inputRef.current.focus();
    }

    setHoverable(false);
  };

  const goDown = () => {
    const fred = fredRef.current;
    const hoveredResult = fred.querySelector(':scope .result:hover');
    const focusedResult = fred.querySelector(':scope .result:focus');
    const selectedResult = focusedResult || hoveredResult;

    if (selectedResult && selectedResult.nextElementSibling) {
      selectedResult.nextElementSibling.focus();
    } else if (fred.querySelector(':scope .result') && !selectedResult) {
      fred.querySelector(':scope .result').focus(); // focusing the first element
    }

    setHoverable(false);
  };

  const onKeyDown = event => {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        goUp();
        break;

      case `ArrowDown`:
        event.preventDefault();
        goDown();
        break;

      case 'Tab':
        event.preventDefault();
        if (event.shiftKey) {
          goUp();
        } else {
          goDown();
        }
    }
  };

  const onMouseMove = e => {
    if (
      FredController.cache.lastMouseX !== e.clientX ||
      FredController.cache.lastMouseY !== e.clientY
    ) {
      // User is presumed to be in "mouse selection" mode.
      FredController.cache.lastMouseX = e.clientX;
      FredController.cache.lastMouseY = e.clientY;
      setHoverable(true);
    } else if (document.activeElement.tagName !== 'INPUT') {
      // This is a hack to keep focus in the main input.
      inputRef.current.focus();
    }
  };

  if (tab === FredController.TAB_COMMAND_RUNNER) {
    return (
      <ControlledComponent controller={controller.commandController}>
        <Command />
      </ControlledComponent>
    );
  } else
    return (
      <div
        className={classNames({
          'ask-fred': true,
          'ask-fred--search': true,
          'ask-fred--empty': query.length <= 1,
          'ask-fred--hoverable': hoverable,
        })}
        ref={fredRef}
        onKeyDown={onKeyDown}
        onMouseMove={onMouseMove}
      >
        <Modal.Header>
          <div className='ask-fred__header'>
            <i
              className={
                tab === FredController.TAB_COMMANDS
                  ? 'ask-fred__icon--command fa-regular fa-angle-right'
                  : 'ask-fred__icon--search fa-regular fa-magnifying-glass'
              }
            />
            <SemiControlledInput
              ref={inputRef}
              type='text'
              value={query}
              onChange={e => controller.actionSetQuery(e.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder={
                tab === FredController.TAB_COMMANDS
                  ? 'Enter command'
                  : 'Search records'
              }
              autoFocus
              className={classNames({
                disguised:
                  tab === FredController.TAB_RECENT && query.length > 0,
              })}
              id='fred-search'
              autoComplete='off'
            />
            {window.currentAccount?.hasGeneralAiAssistant ? (
              <a
                tabIndex={-1}
                href='#'
                onClick={e => {
                  e.preventDefault();
                  controller.actionTransferToAIAssistant();
                }}
                className='ai-feature-button ai-assistant-link'
              >
                Use AI assistant
                <aha-ai-icon class='tw-ml-1' />
              </a>
            ) : (
              renderIf(query.length > 0)(
                <button
                  tabIndex={-1}
                  onClick={() => controller.actionClearQuery()}
                  className='clear-search btn btn-link'
                >
                  Clear
                </button>
              )
            )}
            <button
              tabIndex={-1}
              onClick={() => controller.actionClose()}
              className='close btn btn-link'
            >
              <i className='fa-regular fa-xmark' />
            </button>
          </div>

          <FlushTabs
            activeKey={tab}
            onSelect={tab => controller.actionSelectTab(tab.key)}
            id='search-tabs'
            className={classNames({
              'no-query': query.length === 0,
            })}
            tabs={[
              { key: FredController.TAB_RECENT, title: 'Recent' },
              { key: FredController.TAB_SEARCH_ACCOUNT, title: 'Account' },
              pageInfo &&
              pageInfo.screen_name &&
              pageInfo.screen_name !== 'My work'
                ? {
                    key: FredController.TAB_SEARCH_CONTEXT,
                    title: pageInfo.screen_name,
                  }
                : null,
              pageInfo && pageInfo.screen_name === 'My work'
                ? {
                    key: FredController.TAB_SEARCH_MY_WORK,
                    title: 'My work',
                  }
                : null,
              window.currentAccount?.teamworkOrDevelopFlavor
                ? { key: FredController.TAB_COMMANDS, title: 'Commands' }
                : null,
            ].filter(Boolean)}
          />
        </Modal.Header>

        <Modal.Body>
          {renderIf(tab === FredController.TAB_RECENT)(
            <MemoizedRecentViews query={debouncedQuery} />
          )}
          {renderIf(tab === FredController.TAB_SEARCH_CONTEXT)(
            <MemoizedInContextSearch query={debouncedQuery} />
          )}
          {renderIf(tab === FredController.TAB_SEARCH_ACCOUNT)(
            <AccountSearch query={query} closeModal={controller.actionClose} />
          )}
          {renderIf(tab === FredController.TAB_SEARCH_MY_WORK)(
            <MemoizedMyWorkSearch query={debouncedQuery} />
          )}
          {renderIf(tab === FredController.TAB_COMMANDS)(
            <Commands // already memoized because it is wrapped in 'view()' from mvc.
              query={debouncedQuery}
              initialCommand={initialCommand}
              initialCommandArgs={initialCommandArgs}
              callback={callback}
            />
          )}
        </Modal.Body>
      </div>
    );
};

export default view(Fred);
